import _ from 'lodash'

import {Jurisdiction} from '@/utils/countries/constants'
import {Currency} from '@/utils/currency'
import {DiscountCouponFragment} from '@/utils/graphqlSdk'
import {billingEntities} from '@/utils/pricing/constants'
import {
  calculateCountryDiscount,
  calculatePackageDiscounts,
  calculateVatStatus,
  createDiscountCouponItems,
  createE2eProtectionItem,
  createFeeItem,
  PricingRequest,
  sumFees,
  sumPrices,
} from '@/utils/pricing/helpers'
import {prices} from '@/utils/pricing/prices'
import {
  BillingEntityId,
  CountriesPrice,
  CountryFees,
  Fee,
  FeesGroup,
  Price,
  PricesGroup,
  WithBreakpoints,
} from '@/utils/pricing/types'

import {add, div, mul, sub} from './decimals'

export const partnerFeesCurrency = 'EUR'

// function must be exported to be testable by unit tests
export const calculateBreakpointPriceItems = (
  breakpoints: Array<number>,
  breakpointPrices: PricesGroup<string>,
  breakpointIdPrefix: string,
  currency: Currency,
  count: number,
): Array<{breakpoint: number; count: number; price: number}> =>
  _.orderBy(breakpoints, (x) => x, ['desc']) // sort breakpoints descending
    .filter((breakpoint) => breakpoint <= count) // filter out breakpoints greater than our count
    .map((breakpoint, index, _breakpoints) => ({
      breakpoint,
      count:
        count - (index > 0 ? count - _breakpoints[index - 1] + 1 : 0) - _breakpoints[index] + 1,
      price: breakpointPrices[`${breakpointIdPrefix}.${breakpoint}`][currency],
    }))

// sums the breakpoint-tier price for a given count into a single amount - used where a per-unit
// (class/trademark/...) breakpoint fee needs to collapse into one combined base+tiers fee
export const calculateBreakpointFeeTotal = (
  priceList: WithBreakpoints<PricesGroup<string>, string>,
  jurisdiction: string,
  currency: Currency,
  count: number,
): number =>
  calculateBreakpointPriceItems(
    priceList.breakpoints[jurisdiction] ?? [],
    priceList,
    jurisdiction,
    currency,
    count,
  )
    .map((item) => mul(item.price, item.count))
    .reduce((a, b) => add(a, b), 0)

// Service fees
const calculateCountryMargins = (
  country: Jurisdiction,
  currency: Currency,
  numOfCategories: number,
  vatRate: number,
): FeesGroup => {
  const margins = prices.registrationMargins

  const fees = [
    ...calculateCountryCategoriesMargins(country, currency, numOfCategories, vatRate),
    createFeeItem(`country-margin-description`, margins[country][currency], 1, vatRate, false),
  ]

  return {
    fees,
    total: sumFees(fees),
  }
}

const calculateCountryFees = (
  country: Jurisdiction,
  currency: Currency,
  numOfClasses: number,
): FeesGroup => {
  const fees = [
    ...calculateCountryCategoriesFees(country, currency, numOfClasses),
    createFeeItem(
      `country-fee-description`,
      prices.registrationFees[country][currency],
      1,
      0,
      true,
    ),
  ]

  return {
    fees,
    total: sumFees(fees),
  }
}

// Filing fees
const calculateCountryCategoriesFees = (
  jurisdiction: Jurisdiction,
  currency: Currency,
  numOfClasses: number,
): Fee[] =>
  calculateBreakpointPriceItems(
    prices.categoryFees.breakpoints[jurisdiction],
    prices.categoryFees,
    jurisdiction,
    currency,
    numOfClasses,
  ).map((item) => createFeeItem('category-fee-description', item.price, item.count, 0, true))

const calculateCountryCategoriesMargins = (
  jurisdiction: Jurisdiction,
  currency: Currency,
  numOfClasses: number,
  vatRate: number,
) =>
  calculateBreakpointPriceItems(
    prices.categoryMargins.breakpoints[jurisdiction],
    prices.categoryMargins,
    jurisdiction,
    currency,
    numOfClasses,
  ).map((item) =>
    createFeeItem('category-margin-description', item.price, item.count, vatRate, false),
  )

const calculateCountryPartnerCategoryFees = (
  jurisdiction: Jurisdiction,
  currency: Currency,
  numOfClasses: number,
) =>
  calculateBreakpointPriceItems(
    prices.partnerCategoryFees.breakpoints[jurisdiction],
    prices.partnerCategoryFees,
    jurisdiction,
    currency,
    numOfClasses,
  ).map((item) =>
    createFeeItem('partner-category-fee-description', item.price, item.count, 0, false),
  )

const calculateCountryPartnerFees = (
  jurisdiction: Jurisdiction,
  currency: Currency,
  numOfCategories: number,
): FeesGroup => {
  const fees = [
    ...calculateCountryPartnerCategoryFees(jurisdiction, currency, numOfCategories),
    createFeeItem(
      'partner-fee-description',
      prices.partnerFees[jurisdiction][currency],
      1,
      0,
      false,
    ),
  ]

  return {
    fees,
    total: {
      ...sumFees(fees),
    },
    currency,
  }
}

export const calculateCountryPrice = (
  country: Jurisdiction,
  currency: Currency,
  numOfServices: number,
  discount: number,
  vatRate: number,
): CountryFees => {
  const legalFees = calculateCountryMargins(country, currency, numOfServices, vatRate)
  const registrationFees = calculateCountryFees(country, currency, numOfServices)
  const partnerFees = calculateCountryPartnerFees(country, partnerFeesCurrency, numOfServices)

  const discounts = calculateCountryDiscount(legalFees.total.vatBase, discount, vatRate)

  return {
    country,
    legalFees,
    registrationFees,
    partnerFees,
    discounts,
    legalFeesTotal: sumFees([legalFees, discounts]),
    registrationFeesTotal: registrationFees.total,
    partnerFeesTotal: {
      ...partnerFees.total,
      currency: partnerFees.currency ?? partnerFeesCurrency,
    },
    total: sumFees([legalFees, registrationFees, discounts]),
  }
}

export const calculateCountriesPrice = (
  orderCountries: Jurisdiction[],
  coupon: DiscountCouponFragment | null,
  applyE2eProtection: Boolean,
  currency: Currency,
  numOfServices: number,
  vatRate: number,
): CountriesPrice => {
  const packageDiscounts = calculatePackageDiscounts(orderCountries)
  const countriesPrice = orderCountries.map((country) =>
    calculateCountryPrice(
      country,
      currency,
      numOfServices,
      !coupon ? packageDiscounts[country] ?? 0 : 0,
      vatRate,
    ),
  )

  const legalFeesTotal = sumPrices(countriesPrice.map(({legalFeesTotal}) => legalFeesTotal))

  const registrationFeesTotal = sumPrices(
    countriesPrice.map(({registrationFeesTotal}) => registrationFeesTotal),
  )

  const couponDiscounts = coupon
    ? createDiscountCouponItems(
        legalFeesTotal.vatBase,
        registrationFeesTotal.vatBase,
        coupon,
        vatRate,
      )
    : []

  const couponDiscountsTotal = sumFees(couponDiscounts)
  const subTotal = sumFees([...countriesPrice, ...couponDiscounts])
  const e2eProtection = applyE2eProtection ? [createE2eProtectionItem(subTotal, vatRate)] : []
  const total = sumFees([...countriesPrice, ...couponDiscounts, ...e2eProtection])

  const partnerFeesTotal = sumPrices(
    countriesPrice.map(
      ({partnerFeesTotal}) => partnerFeesTotal ?? {vat: 0, vatBase: 0, withVat: 0},
    ),
  )

  return {
    countries: countriesPrice,
    couponDiscounts,
    e2eProtection,
    couponDiscountsTotal,
    legalFeesTotal,
    registrationFeesTotal,
    partnerFeesTotal: {
      ...partnerFeesTotal,
      currency: partnerFeesCurrency,
    },
    total: _.mapValues(total, (value) => Math.max(value, 0)),
  }
}

export const getNumberOfServices = (services: any) => {
  return Object.keys(services || {}).length ?? 0
}

export const calculatePrice = (
  request: PricingRequest,
  coupon: DiscountCouponFragment | null,
  applyE2eProtection: Boolean,
  forceVatIncluded: boolean,
  billingEntityId: BillingEntityId = 'SK',
): Price => {
  const currency = request.currency as Currency
  const billingEntity = billingEntities[billingEntityId]
  const billingInfo = request.billing_entity
  const jurisdictions = (request.jurisdictions as Jurisdiction[]) ?? []

  const {vatIncluded, reverseVatCharge} = calculateVatStatus(
    billingEntity,
    billingInfo,
    forceVatIncluded,
  )
  const vatRate = forceVatIncluded || vatIncluded ? billingEntity.vatRate : 0

  // do not apply coupon if there are no countries as we do not want to show negative price
  // TODO: ideally we should apply coupon and make sure the total price is not negative
  const countriesPrice = calculateCountriesPrice(
    jurisdictions,
    jurisdictions.length ? coupon : null,
    applyE2eProtection,
    currency,
    request.numberOfServices,
    vatRate,
  )

  return {
    ...countriesPrice,
    currency,
    vatIncluded,
    reverseVatCharge,
    vatRate,
  }
}

export const calculateOfferingPrice = (
  countries: Jurisdiction[],
  currency: Currency,
  applyDiscount = true,
): {legalFee: number; registrationFee: number; total: number} => {
  const discounts = calculatePackageDiscounts(countries)
  const registrationFee = countries
    .map((country) => prices.registrationFees[country][currency])
    .reduce((a, b) => add(a, b), 0)

  const margins = prices.registrationMargins

  const legalFee = countries
    .map((country) =>
      applyDiscount && discounts[country]
        ? mul(margins[country][currency], sub(1, div(discounts[country]!, 100)))
        : margins[country][currency],
    )
    .reduce((a, b) => add(a, b), 0)

  return {
    legalFee,
    registrationFee,
    total: add(legalFee, registrationFee),
  }
}

export const calculateCategoriesPrice = (
  countries: Jurisdiction[],
  numberOfServices: number,
  currency: Currency,
  vatRate = 0,
) => {
  const registrationFee = sumFees([
    ...countries.flatMap((country) =>
      calculateCountryCategoriesFees(country, currency, numberOfServices),
    ),
  ])

  const legalFee = sumFees([
    ...countries.flatMap((country) =>
      calculateCountryCategoriesMargins(country, currency, numberOfServices, vatRate),
    ),
  ])

  const total = sumPrices([legalFee, registrationFee])

  return {
    legalFee,
    registrationFee,
    total,
  }
}

export const calculateAdditionalClassesPrice = (
  countries: Jurisdiction[],
  numOfClasses: number,
  currency: Currency,
  vatRate = 0,
): number =>
  sub(
    calculateCategoriesPrice(countries, numOfClasses + 1, currency, vatRate).total.withVat,
    calculateCategoriesPrice(countries, numOfClasses, currency, vatRate).total.withVat,
  )

export const getCategoryFees = (
  country: Jurisdiction,
  currency: Currency,
): Record<number, number> => {
  const categoryMargins = prices.categoryMargins

  return prices.categoryFees.breakpoints[country].reduce((acc, point) => {
    return {
      ...acc,
      [point]: add(
        prices.categoryFees[`${country}.${point}`][currency],
        categoryMargins[`${country}.${point}`][currency],
      ),
    }
  }, {})
}
